function normalizeValue(value) {
  return String(value || "")
    .trim()
    .toLowerCase();
}

function safeCheck(check, fallback = false) {
  if (typeof check !== "function") {
    return fallback;
  }

  try {
    return check() === true;
  } catch (error) {
    console.error(
      "SHIFT ELIGIBILITY CHECK ERROR",
      error
    );

    return false;
  }
}

export function getWorkerIdentity(worker) {
  return String(
    worker?.uid ||
      worker?.id ||
      worker?.userId ||
      ""
  ).trim();
}

export function getShiftIdentity(shift) {
  return String(
    shift?.id ||
      shift?.shiftId ||
      ""
  ).trim();
}

export function getShiftRole(shift) {
  return normalizeValue(
    shift?.role ||
      shift?.position ||
      shift?.requiredRole ||
      ""
  );
}

/*
 * Evaluates one operational worker
 * against one specific shift.
 *
 * The Planner's existing business-rule
 * functions will be injected into this
 * engine during the next step.
 *
 * That prevents duplicate credential,
 * availability, or overlap logic.
 */
export function evaluateWorkerForShift({
  worker,
  shift,
  allShifts = [],

  roleCheck,
  credentialCheck,
  availabilityCheck,
  overlapCheck,
} = {}) {
  const reasons = [];

  const workerUid =
    getWorkerIdentity(worker);

  const shiftId =
    getShiftIdentity(shift);

  const shiftRole =
    getShiftRole(shift);

  if (!worker) {
    return {
      eligible: false,

      workerUid: "",
      shiftId,
      shiftRole,

      reasons: [
        "worker_missing",
      ],

      checks: {
        role: false,
        credential: false,
        availability: false,
        overlap: false,
      },
    };
  }

  if (!shift) {
    return {
      eligible: false,

      workerUid,
      shiftId: "",
      shiftRole: "",

      reasons: [
        "shift_missing",
      ],

      checks: {
        role: false,
        credential: false,
        availability: false,
        overlap: false,
      },
    };
  }

  const roleMatches =
    safeCheck(
      () =>
        roleCheck(
          worker,
          shift
        )
    );

  if (!roleMatches) {
    reasons.push(
      "role_mismatch"
    );
  }

  const credentialMatches =
    safeCheck(
      () =>
        credentialCheck(
          worker,
          shift
        )
    );

  if (!credentialMatches) {
    reasons.push(
      "credential_missing_or_invalid"
    );
  }

  const isAvailable =
    safeCheck(
      () =>
        availabilityCheck(
          worker,
          shift
        )
    );

  if (!isAvailable) {
    reasons.push(
      "worker_unavailable"
    );
  }

  /*
   * overlapCheck should return TRUE when
   * the worker has a conflicting shift.
   *
   * Therefore eligibility requires FALSE.
   */
  const hasOverlap = !safeCheck(
    () =>
      typeof overlapCheck === "function" &&
      overlapCheck(worker, shift, allShifts) === false
  );

  if (hasOverlap) {
    reasons.push(
      "shift_overlap"
    );
  }

  const checks = {
    role: roleMatches,
    credential:
      credentialMatches,
    availability:
      isAvailable,
    overlap:
      !hasOverlap,
  };

  return {
    workerUid,
    shiftId,
    shiftRole,

    eligible:
      reasons.length === 0,

    reasons,
    checks,
  };
}

/*
 * Evaluate an entire operational
 * worker pool against one shift.
 */
export function getShiftEligibilityResults({
  workers = [],
  shift,
  allShifts = [],

  roleCheck,
  credentialCheck,
  availabilityCheck,
  overlapCheck,
} = {}) {
  const rows =
    Array.isArray(workers)
      ? workers
      : [];

  return rows.map((worker) => ({
    worker,

    ...evaluateWorkerForShift({
      worker,
      shift,
      allShifts,

      roleCheck,
      credentialCheck,
      availabilityCheck,
      overlapCheck,
    }),
  }));
}

/*
 * Return only workers who passed every
 * required eligibility check.
 */
export function getEligibleWorkersForShift(
  options = {}
) {
  return getShiftEligibilityResults(
    options
  )
    .filter(
      (result) =>
        result.eligible === true
    )
    .map(
      (result) =>
        result.worker
    );
}

/*
 * Useful for Shift 360 and AI Assist.
 *
 * Example:
 *
 * {
 *   eligible: 7,
 *   ineligible: 4,
 *   roleMismatch: 1,
 *   credentialIssue: 1,
 *   unavailable: 1,
 *   overlap: 1
 * }
 */
export function summarizeShiftEligibility(
  results = []
) {
  const rows =
    Array.isArray(results)
      ? results
      : [];

  return {
    total:
      rows.length,

    eligible:
      rows.filter(
        (row) =>
          row?.eligible === true
      ).length,

    ineligible:
      rows.filter(
        (row) =>
          row?.eligible !== true
      ).length,

    roleMismatch:
      rows.filter((row) =>
        row?.reasons?.includes(
          "role_mismatch"
        )
      ).length,

    credentialIssue:
      rows.filter((row) =>
        row?.reasons?.includes(
          "credential_missing_or_invalid"
        )
      ).length,

    unavailable:
      rows.filter((row) =>
        row?.reasons?.includes(
          "worker_unavailable"
        )
      ).length,

    overlap:
      rows.filter((row) =>
        row?.reasons?.includes(
          "shift_overlap"
        )
      ).length,
  };
}